import { DocumentTextIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { formatAmount } from "@utils";
import { cn } from "@utils";
import Tooltip from "@components/Tooltip";
import { buildAgedDebt } from "./agedDebt";

// `colors` optionally overrides the bucket colours by bucket key.
export default function AgedDebtCard({ customer, isLoading, colors }) {
  const { total, buckets, unallocatedCash, totalBalance } = buildAgedDebt(customer);
  const colorFor = (bucket) => colors?.[bucket.key] ?? bucket.color;

  const row = (label, value, { emphasis = false } = {}) => (
    <div
      className={cn(
        "flex items-center justify-between gap-6",
        emphasis && "border-t border-zinc-100 pt-1.5",
      )}
    >
      <span className={emphasis ? "font-semibold text-zinc-900" : "text-zinc-500"}>{label}</span>
      <span
        className={cn(
          emphasis
            ? "font-semibold text-zinc-900"
            : value < 0
              ? "text-green-700"
              : "text-zinc-900",
        )}
      >
        {formatAmount(value)}
      </span>
    </div>
  );

  const breakdown = (
    <div className="w-72 space-y-1.5">
      {row("Invoices & credit notes", total)}
      {row("Unallocated cash", unallocatedCash)}
      {row("Total balance", totalBalance, { emphasis: true })}
    </div>
  );

  return (
    <div className="rounded-2xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-zinc-500">
          <DocumentTextIcon aria-hidden="true" className="size-4" />
          All invoices by due date
        </span>
        <span className="text-lg font-semibold text-zinc-900">
          {isLoading ? "—" : formatAmount(totalBalance)}
        </span>
      </div>

      {!isLoading && (
        <div className="mt-1 flex items-center justify-end gap-1 text-xs text-zinc-400">
          <span>Including credit notes and unallocated cash</span>
          <Tooltip
            content={breakdown}
            wrapperClassName="inline-flex shrink-0"
            placement="bottom"
            variant="card"
          >
            <InformationCircleIcon aria-hidden="true" className="size-3.5" strokeWidth={2} />
          </Tooltip>
        </div>
      )}

      {/* Proportional bucket bar */}
      <div className="mt-3 flex h-1.5 gap-1 overflow-hidden rounded-full">
        {buckets
          .filter((bucket) => bucket.amount > 0)
          .map((bucket) => (
            <span
              key={bucket.key}
              className={cn("h-full rounded-full", colorFor(bucket))}
              style={{ width: `${Math.max(bucket.percent, 4)}%` }}
            />
          ))}
        {total === 0 && <span className="h-full w-full rounded-full bg-zinc-100" />}
      </div>

      <div className="mt-3 grid grid-cols-4 gap-3">
        {buckets.map((bucket) => (
          <div key={bucket.key} className="min-w-0">
            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
              <span className={cn("size-1.5 shrink-0 rounded-full", colorFor(bucket))} />
              <span className="truncate">{bucket.label}</span>
            </div>
            <div className="mt-0.5 text-sm font-semibold text-zinc-900">
              {formatAmount(bucket.amount)}
            </div>
            <div className="text-xs text-zinc-400">{bucket.percent}%</div>
          </div>
        ))}
      </div>
    </div>
  );
}
